import * as _ from 'underscore';

import { ButtonType } from '@tableau/widgets';
import { Messages } from '../../codegen/Localize';
import { CheckboxState } from '../../common/components/checkboxes/CheckboxState';
import { ButtonWithSpinner } from '../../common/react/components/ButtonWithSpinner';
import { MultilineTextField } from '../../common/react/components/MultilineTextField';
import { UsernameHelpers } from '../helpers/UsernameHelpers';
import { IUserInfo } from '../models/IUserInfo';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { ServerUsers } from '../resources/ServerUsers';
import { SiteRolesResource } from '../resources/SiteRoles';
import { IImportUsersFromActiveDirectoryResult } from '../ServerApi/requests/ImportUsersFromActiveDirectoryRequest';
import { IUpdateServerUsersSiteMembershipResult } from '../ServerApi/requests/UpdateServerUsersSiteMembershipRequest';
import { UserAddedEvent } from '../services/AddUsersOptionsAction';
import { SiteRolesPickerClientService } from '../services/SiteRolesPickerClientService';
import {
  SiteMembershipUpdateActionNotification,
  UserActionNotification,
} from '../services/UserActionNotification';
import { ISiteRoleChanges } from './SiteRolesPickerCtrl';

export class AddActiveDirectoryServerUsersDialogCtrl {

  public static $inject = [
    '$scope',
    '$q',
    'SiteMembershipUpdateActionNotification',
    'SiteRoles',
    'SiteRolesPickerClientService',
    'ServerUsers',
    'UserActionNotification'
  ];

  constructor(
    private $scope: AddActiveDirectoryServerUsersDialogCtrl.Scope,
    private $q: ng.IQService,
    private siteMembershipUpdateActionNotification: SiteMembershipUpdateActionNotification,
    siteRoles: SiteRolesResource,
    private rolesPickerClient: SiteRolesPickerClientService,
    private serverUsers: ServerUsers,
    private userActionNotification: UserActionNotification,
  ) {

    // site role changes will be stored here
    this.$scope.siteRoleChanges = {
      siteRoles: {},
      serverAdmin: undefined
    };

    this.$scope.inputText = '';

    this.$scope.$watch('busy', () => {
      this.$scope.submitButtonProps = {
        ...this.$scope.submitButtonProps,
        disabled: $scope.busy,
        showSpinner: $scope.busy
      };
      this.$scope.cancelButtonProps = {
        ...this.$scope.cancelButtonProps,
        disabled: $scope.busy
      };
      this.$scope.userTextFieldProps = {
        ...this.$scope.userTextFieldProps,
        disabled: $scope.busy
      };
    });

    this.$scope.$watch('inputText', () => {
      this.$scope.submitButtonProps = {
        ...this.$scope.submitButtonProps,
        disabled: $scope.inputText.length === 0
      };
    });

    this.$scope.submitButtonProps = {
      buttonType: ButtonType.Go,
      showSpinner: false,
      clicked: () => this.importUsers(this.$scope.inputText),
      testId: 'confirm-action-dialog-confirm',
      title: Messages.importUsers_Submit(),
      disabled: false
    };

    this.$scope.cancelButtonProps = {
      buttonType: ButtonType.Outline,
      showSpinner: false,
      clicked: () => this.$scope.$apply(() => this.$scope.dismiss()),
      testId: 'confirm-action-dialog-cancel',
      title: Messages.cancel(),
      disabled: false
    };

    this.$scope.userTextFieldProps = {
      handleChange: (userText: string) => this.$scope.$apply(() => {this.$scope.inputText = userText;}),
      disabled: $scope.busy
    };

    // delete this? linter doesn't think it gets used anywhere...
    // I have absolutely no confidence that we have any tests that would let us know if something broke here
    const changes = (siteRoleChanges: ISiteRoleChanges): void => {
      this.$scope.siteRoleChanges = siteRoleChanges;
    };
  }

  public importUsers(userTextInput: string): ng.IPromise<any> {
    this.$scope.busy = true;
    this.$scope.usersToAdd = UsernameHelpers.splitUsernames(userTextInput); // match strings delimited by semicolons & commas

    return this.serverUsers.importUsersFromActiveDirectory(this.$scope.usersToAdd).then(
      (result: IImportUsersFromActiveDirectoryResult) => {
        const users = Set.fromIdItemArray(result.users);
        let promise = this.$q.when({});

        if (!this.$scope.siteRoleChanges.serverAdmin && _.isEmpty(this.$scope.siteRoleChanges.siteRoles)) {
          // if no site membership or server admin has been selected, toast for adding the users to the server
          this.userActionNotification.notify({
            users: users,
            results: result.results,
            errors: result.errors,
            successCode: '10'
          });
        } else {
          // otherwise, we will toast based on the site membership results
          promise = this.setRoles(users).then(() => {
            if (result.errors && result.errors.length > 0) {
              // if there were any errors creating the users on the server, we need to notify about those.
              this.userActionNotification.notifyErrors({
                users: users,
                errors: result.errors
              });
            }
            return {};
          });
        }

        return promise.then(() => this.$scope.$emit(UserAddedEvent));
      })
    // close the dialog
    .then(() => this.$scope.close({}));
  }

  /**
  * adds the users that were sucessfully created on the server to any sites and roles (including server admin)
  * that were selected in the site roles picker.
  */
  private setRoles(users: Set<IUserInfo>): ng.IPromise<any> {
    if (users.isEmpty()) {
      return this.$q.when({});
    } else if (this.$scope.siteRoleChanges.serverAdmin) {
      return this.updateServerAdmin(users);
    } else if (!_.isEmpty(this.$scope.siteRoleChanges.siteRoles)) {
      return this.updateSiteMembership(users);
    }
  }

  // section: update server admin

  private updateServerAdmin(users: Set<IUserInfo>): ng.IPromise<any> {
    const newAdminState = (this.$scope.siteRoleChanges.serverAdmin === CheckboxState.checked);
    return this.rolesPickerClient.updateServerAdmin(users, newAdminState);
  }

  // section: update site membership or roles

  private updateSiteMembership(users: Set<IUserInfo>): ng.IPromise<any> {
    const userIds = users.keys();
    const params = this.rolesPickerClient.siteRoleChangesToParams(userIds, this.$scope.siteRoleChanges.siteRoles);
    return this.rolesPickerClient.updateServerUsersSiteMembership(params).then((result: IUpdateServerUsersSiteMembershipResult) => {
      if(result.results){
        this.siteMembershipUpdateActionNotification.notify(
            result.results,
            users,
            this.$scope.siteRoleChanges.siteRoles);
      }
    });
  }

}

export module AddActiveDirectoryServerUsersDialogCtrl {

  export interface Scope extends ng.IScope {
    busy: boolean;
    close: (result: any) => void;
    dismiss: (reason?: any) => void;
    usersToAdd: string[];
    siteRoleChanges: ISiteRoleChanges;
    serverUsers: ServerUsers;
    cancelButtonProps: ButtonWithSpinner.Props;
    submitButtonProps: ButtonWithSpinner.Props;
    inputText: string;
    userTextFieldProps: MultilineTextField.Props;
  }

}

Module.instance().controller('AddActiveDirectoryServerUsersDialogCtrl', AddActiveDirectoryServerUsersDialogCtrl);
